import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  CorrectionActorKindSchema,
  CorrectionContextSchema,
  CorrectionFieldSchema,
  CorrectionSourceSchema,
  HarnessActionChangeSchema,
  HarnessActionEntitySchema,
  HarnessActionPreviewSchema,
  HarnessActionToolResultSchema,
  MemoryModuleSchema,
  CORRECTION_NOTE_MAX_LENGTH,
  MemoryProposalKindSchema,
  MemoryProposalPayloadSchema,
  MemoryProposalSourceSchema,
  MemoryProposalStatusSchema,
  MemoryRuleKindSchema,
  MemoryRulePayloadSchema,
  MemoryRuleSourceSchema,
  MemoryRuleStatusSchema,
  MemoryRuleUsageEffectSchema,
  MemoryRuleUsageStageSchema,
} from "@erp/shared-types";

/**
 * Agent harness — memory rules, proposals, correction events and per-draft
 * influence. Organization scope and reviewer identity always come from the
 * authenticated session, never from input.
 */

const Pagination = {
  limit: z.number().int().min(1).max(100).default(25),
  offset: z.number().int().min(0).default(0),
};

/** Human-readable names for ids a payload references (customers, locations). */
export const HarnessLabelsSchema = z.record(z.string(), z.string());

/**
 * The record a correction was made on: its module, type and id, and a label
 * to show (a draft's PO number, a cash receipt's payer). The frontend builds
 * the link from the module.
 */
export const CorrectionRecordSchema = z.object({
  module: MemoryModuleSchema,
  entityType: z.string(),
  entityId: z.uuid(),
  label: z.string().nullable(),
});
export type CorrectionRecordDto = z.infer<typeof CorrectionRecordSchema>;

export const MemoryRuleSchema = z.object({
  id: z.uuid(),
  module: MemoryModuleSchema,
  kind: MemoryRuleKindSchema,
  scopeCustomerId: z.uuid().nullable(),
  matchKey: z.string(),
  payload: MemoryRulePayloadSchema,
  status: MemoryRuleStatusSchema,
  version: z.number().int(),
  supersededByRuleId: z.uuid().nullable(),
  source: MemoryRuleSourceSchema,
  proposalId: z.uuid().nullable(),
  createdByUserId: z.uuid().nullable(),
  activatedByUserId: z.uuid().nullable(),
  activatedAt: z.iso.datetime().nullable(),
  retiredAt: z.iso.datetime().nullable(),
  retiredReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  labels: HarnessLabelsSchema,
});
export type MemoryRuleDto = z.infer<typeof MemoryRuleSchema>;

export const MemoryRuleUsageSchema = z.object({
  id: z.uuid(),
  ruleId: z.uuid(),
  ruleVersion: z.number().int(),
  entityType: z.string(),
  entityId: z.uuid(),
  stage: MemoryRuleUsageStageSchema,
  effect: MemoryRuleUsageEffectSchema,
  field: z.string().nullable(),
  beforeValue: z.unknown().nullable(),
  afterValue: z.unknown().nullable(),
  createdAt: z.iso.datetime(),
});
export type MemoryRuleUsageDto = z.infer<typeof MemoryRuleUsageSchema>;

/**
 * A rule usage with the record it was applied to, for linking and labelling.
 * Optional as well as nullable: a build from before `record` existed can
 * still answer during a rolling deploy, and the UI then shows the bare id.
 */
export const MemoryRuleUsageWithRecordSchema = MemoryRuleUsageSchema.extend({
  record: z.preprocess(
    (value) => value ?? null,
    CorrectionRecordSchema.nullable(),
  ),
});
export type MemoryRuleUsageWithRecordDto = z.infer<
  typeof MemoryRuleUsageWithRecordSchema
>;

export const MemoryProposalEvidenceSchema = z.object({
  correctionEventId: z.uuid().nullable(),
  draftSalesOrderId: z.uuid().nullable(),
  threadId: z.uuid().nullable(),
  /** Present when the evidence is a correction event. */
  field: CorrectionFieldSchema.nullable(),
  beforeValue: z.unknown().nullable(),
  afterValue: z.unknown().nullable(),
  purchaseOrderNumber: z.string().nullable(),
  /** The corrected record, when the evidence is a correction event. */
  record: CorrectionRecordSchema.nullable(),
  /** Why the person made the change, when they said. */
  note: z.string().nullable(),
  at: z.iso.datetime().nullable(),
});
export type MemoryProposalEvidenceDto = z.infer<
  typeof MemoryProposalEvidenceSchema
>;

export const MemoryProposalSchema = z.object({
  id: z.uuid(),
  module: MemoryModuleSchema,
  kind: MemoryProposalKindSchema,
  scopeCustomerId: z.uuid().nullable(),
  matchKey: z.string(),
  payload: MemoryProposalPayloadSchema,
  title: z.string(),
  rationale: z.string(),
  supportCount: z.number().int(),
  contradictionCount: z.number().int(),
  firstSeenAt: z.iso.datetime().nullable(),
  lastSeenAt: z.iso.datetime().nullable(),
  status: MemoryProposalStatusSchema,
  reviewRevision: z.number().int(),
  source: MemoryProposalSourceSchema,
  createdByUserId: z.uuid().nullable(),
  decidedByUserId: z.uuid().nullable(),
  decidedAt: z.iso.datetime().nullable(),
  rejectionReason: z.string().nullable(),
  ruleId: z.uuid().nullable(),
  /** The active rule this proposal would supersede, if any. */
  currentRule: MemoryRuleSchema.nullable(),
  evidence: z.array(MemoryProposalEvidenceSchema),
  labels: HarnessLabelsSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type MemoryProposalDto = z.infer<typeof MemoryProposalSchema>;

export const AnnotateCorrectionInputSchema = z.object({
  /** Only modules whose corrections are customer changes on a record. */
  module: z.enum(["cash_receipt", "remittance"]),
  entityId: z.uuid(),
  oneOff: z.boolean(),
  note: z.string().trim().max(CORRECTION_NOTE_MAX_LENGTH).nullable(),
});
export type AnnotateCorrectionInput = z.infer<
  typeof AnnotateCorrectionInputSchema
>;

export const CorrectionEventSchema = z.object({
  id: z.uuid(),
  /** Set for sales-order drafts only; `record` covers every module. */
  draftSalesOrderId: z.uuid().nullable(),
  record: CorrectionRecordSchema.nullable(),
  lineItemId: z.uuid().nullable(),
  field: CorrectionFieldSchema,
  beforeValue: z.unknown().nullable(),
  afterValue: z.unknown().nullable(),
  suggestedValue: z.unknown().nullable(),
  actorUserId: z.uuid().nullable(),
  actorKind: CorrectionActorKindSchema,
  source: CorrectionSourceSchema,
  context: CorrectionContextSchema,
  processedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  purchaseOrderNumber: z.string().nullable(),
  labels: HarnessLabelsSchema,
});
export type CorrectionEventDto = z.infer<typeof CorrectionEventSchema>;

export const DraftInfluenceSchema = z.object({
  /** Rules that bound or excluded something on this draft. */
  applied: z.array(
    z.object({
      usage: MemoryRuleUsageSchema,
      rule: MemoryRuleSchema.nullable(),
    }),
  ),
  /** Reasoning-audit rows the resolvers and agents left for this draft. */
  reasoning: z.array(
    z.object({
      id: z.uuid(),
      action: z.string(),
      resultType: z.string().nullable(),
      resultId: z.string().nullable(),
      confidence: z.string().nullable(),
      reasoning: z.string().nullable(),
      source: z.string().nullable(),
      createdBy: z.string().nullable(),
      createdAt: z.iso.datetime(),
    }),
  ),
  /** Proposals raised from this draft. */
  proposals: z.array(
    z.object({
      id: z.uuid(),
      kind: MemoryProposalKindSchema,
      title: z.string(),
      status: MemoryProposalStatusSchema,
      createdAt: z.iso.datetime(),
    }),
  ),
  /** Human corrections captured on this draft. */
  corrections: z.array(CorrectionEventSchema),
  labels: HarnessLabelsSchema,
});
export type DraftInfluenceDto = z.infer<typeof DraftInfluenceSchema>;

// ---------------------------------------------------------------------------
// Brain: one picture of what the harness has learned and how it connects.
// ---------------------------------------------------------------------------

export const BrainNodeKindSchema = z.enum([
  "agent",
  "category",
  "rule",
  "proposal",
  "note",
  "customer",
  "location",
  "draft",
]);
export type BrainNodeKind = z.infer<typeof BrainNodeKindSchema>;

export const BrainNodeSchema = z.object({
  id: z.string(),
  kind: BrainNodeKindSchema,
  /** Human label (data: names, aliases, PO numbers). Category nodes are labelled client-side from `subject`. */
  label: z.string(),
  sublabel: z.string().nullable(),
  /** The rule / proposal kind for category, rule, proposal and note nodes. */
  subject: MemoryProposalKindSchema.nullable(),
  href: z.string().nullable(),
  /** Relative importance: usage count for rules, support for proposals, 1 otherwise. */
  weight: z.number().int(),
});
export type BrainNodeDto = z.infer<typeof BrainNodeSchema>;

export const BrainEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  label: z.string().nullable(),
});
export type BrainEdgeDto = z.infer<typeof BrainEdgeSchema>;

export const BrainGrowthPointSchema = z.object({
  /** Calendar day `YYYY-MM-DD` (UTC). */
  day: z.string(),
  /** Cumulative counts as of the end of that day. */
  corrections: z.number().int(),
  proposals: z.number().int(),
  rules: z.number().int(),
});

export const BrainRecentKindSchema = z.enum([
  "rule_activated",
  "proposal_raised",
  "correction_captured",
]);
export type BrainRecentKind = z.infer<typeof BrainRecentKindSchema>;
export const BrainRecentSchema = z.object({
  id: z.string(),
  kind: BrainRecentKindSchema,
  subject: MemoryProposalKindSchema.nullable(),
  field: CorrectionFieldSchema.nullable(),
  /** Data-only summary (the label of the rule / proposal, or the PO number). */
  summary: z.string(),
  at: z.iso.datetime(),
  href: z.string().nullable(),
});
export type BrainRecentDto = z.infer<typeof BrainRecentSchema>;

export const BrainSnapshotSchema = z.object({
  stats: z.object({
    activeRules: z.number().int(),
    notes: z.number().int(),
    proposalsPending: z.number().int(),
    correctionsTotal: z.number().int(),
    correctionsLast30Days: z.number().int(),
    draftsInfluenced: z.number().int(),
    entities: z.number().int(),
    lastLearnedAt: z.iso.datetime().nullable(),
  }),
  growth: z.array(BrainGrowthPointSchema),
  graph: z.object({
    nodes: z.array(BrainNodeSchema),
    edges: z.array(BrainEdgeSchema),
  }),
  recent: z.array(BrainRecentSchema),
});
export type BrainSnapshotDto = z.infer<typeof BrainSnapshotSchema>;

export const BrainBootstrapResultSchema = z.object({
  draftsScanned: z.number().int(),
  eventsDerived: z.number().int(),
  eventsWritten: z.number().int(),
  humanCorrections: z.number().int(),
  candidates: z.number().int(),
  proposalsCreatedOrUpdated: z.number().int(),
});
export type BrainBootstrapResultDto = z.infer<
  typeof BrainBootstrapResultSchema
>;

// ---------------------------------------------------------------------------
// Actions — previews the agent produced that wait for the user's Apply, and
// the undo window on direct actions.
// ---------------------------------------------------------------------------

export const HarnessPendingActionStatusSchema = z.enum([
  "created",
  "applying",
  "applied",
  "failed",
  "discarded",
  "expired",
]);
export type HarnessPendingActionStatus = z.infer<
  typeof HarnessPendingActionStatusSchema
>;

export const HarnessPendingActionSchema = z.object({
  id: z.uuid(),
  threadId: z.uuid(),
  toolCallId: z.string(),
  toolName: z.string(),
  status: HarnessPendingActionStatusSchema,
  preview: HarnessActionPreviewSchema,
  entity: HarnessActionEntitySchema.nullable(),
  invalidates: z.array(z.string()),
  expiresAt: z.iso.datetime(),
  appliedAt: z.iso.datetime().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type HarnessPendingAction = z.infer<typeof HarnessPendingActionSchema>;

/** Outcome of Apply or Undo; `failed` carries the same message the page would show. */
export const HarnessActionApplyResultSchema = z.object({
  status: z.enum(["applied", "failed"]),
  toolName: z.string(),
  toolCallId: z.string(),
  summary: z.string(),
  changes: z.array(HarnessActionChangeSchema).optional(),
  entity: HarnessActionEntitySchema.optional(),
  /** Dotted contract paths whose queries the page should refetch. */
  invalidates: z.array(z.string()),
  error: z.string().optional(),
  /** ISO timestamp until which `actions.undo` accepts this call. */
  undoAvailableUntil: z.string().optional(),
  /** One sentence the chat may send on the user's behalf so the model learns the outcome. */
  agentNote: z.string(),
});
export type HarnessActionApplyResult = z.infer<
  typeof HarnessActionApplyResultSchema
>;

// ---------------------------------------------------------------------------
// Tool calls — the ledger behind the Tool Calls page: every chat-agent tool
// call (server or browser executed) by module, page, tool, user and outcome.
// ---------------------------------------------------------------------------

export const HarnessToolCallExecutorSchema = z.enum(["server", "browser"]);
export const HarnessToolCallEffectSchema = z.enum([
  "read",
  "ui",
  "direct",
  "preview",
  "apply",
  "undo",
]);
export const HarnessToolCallStatusSchema = z.enum([
  "running",
  "ok",
  "failed",
  "pending",
  "applied",
  "discarded",
  "expired",
  "undone",
]);

export const HarnessToolCallSchema = z.object({
  id: z.uuid(),
  threadId: z.uuid(),
  toolCallId: z.string(),
  userId: z.uuid(),
  userName: z.string().nullable(),
  moduleKey: z.string(),
  pageKey: z.string().nullable(),
  toolName: z.string(),
  toolType: z.string().nullable(),
  executor: HarnessToolCallExecutorSchema,
  effect: HarnessToolCallEffectSchema,
  status: HarnessToolCallStatusSchema,
  resultSummary: z.string().nullable(),
  errorMessage: z.string().nullable(),
  entityKind: z.string().nullable(),
  entityId: z.string().nullable(),
  pendingActionId: z.uuid().nullable(),
  changes: z.array(HarnessActionChangeSchema).nullable(),
  durationMs: z.number().int().nullable(),
  model: z.string().nullable(),
  traceId: z.string().nullable(),
  undoAvailableUntil: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type HarnessToolCall = z.infer<typeof HarnessToolCallSchema>;

/** Redacted input is only returned on `get` (drill-in), never in lists. */
export const HarnessToolCallDetailSchema = HarnessToolCallSchema.extend({
  input: z.record(z.string(), z.unknown()).nullable(),
});

export const HarnessToolCallFiltersSchema = z.object({
  moduleKey: z.string().optional(),
  pageKey: z.string().optional(),
  toolName: z.string().optional(),
  userId: z.uuid().optional(),
  executor: HarnessToolCallExecutorSchema.optional(),
  effect: HarnessToolCallEffectSchema.optional(),
  status: HarnessToolCallStatusSchema.optional(),
  entityId: z.string().optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});

export const HarnessToolCallSummaryRowSchema = z.object({
  moduleKey: z.string(),
  pageKey: z.string().nullable(),
  toolName: z.string(),
  effect: HarnessToolCallEffectSchema,
  calls: z.number().int(),
  writes: z.number().int(),
  failed: z.number().int(),
  pending: z.number().int(),
  discarded: z.number().int(),
  undone: z.number().int(),
  avgDurationMs: z.number().nullable(),
});

export const HarnessToolCallSummarySchema = z.object({
  totals: z.object({
    calls: z.number().int(),
    writes: z.number().int(),
    failed: z.number().int(),
    pending: z.number().int(),
    users: z.number().int(),
    threads: z.number().int(),
  }),
  rows: z.array(HarnessToolCallSummaryRowSchema),
});
export type HarnessToolCallSummary = z.infer<
  typeof HarnessToolCallSummarySchema
>;

// ---------------------------------------------------------------------------
// Policies — per-organization overrides of how an action tool behaves.
// ---------------------------------------------------------------------------

export const HarnessToolPolicyModeSchema = z.enum([
  "inherit",
  "disabled",
  "preview_required",
]);

/** One row per action tool the harness knows, joined with the org's override. */
export const HarnessToolPolicySchema = z.object({
  toolType: z.string(),
  toolName: z.string(),
  displayName: z.string(),
  description: z.string(),
  moduleKey: z.string(),
  pageKeys: z.array(z.string()),
  defaultEffect: z.enum(["direct", "preview"]),
  mode: HarnessToolPolicyModeSchema,
  requiredPermissions: z.array(
    z.object({ action: z.string(), subject: z.string() }),
  ),
});

export const HarnessToolPoliciesSchema = z.object({
  tools: z.array(HarnessToolPolicySchema),
  maxWritesPerTurn: z.number().int().positive().nullable(),
});
export type HarnessToolPolicies = z.infer<typeof HarnessToolPoliciesSchema>;

// ---------------------------------------------------------------------------
// Recommendations — page-aware, RBAC-scoped suggested actions.
// ---------------------------------------------------------------------------

export const HarnessRecommendationSchema = z.object({
  id: z.string(),
  title: z.string(),
  detail: z.string().nullable(),
  severity: z.enum(["info", "warn", "urgent"]),
  /** The message the chat sends on the user's behalf when the card is clicked. */
  prompt: z.string(),
  toolName: z.string().nullable(),
});
export type HarnessRecommendation = z.infer<typeof HarnessRecommendationSchema>;

export const harnessContracts = {
  rules: {
    list: oc
      .input(
        z.object({
          kind: MemoryRuleKindSchema.optional(),
          customerId: z.uuid().optional(),
          status: MemoryRuleStatusSchema.optional(),
          search: z.string().max(200).optional(),
          ...Pagination,
        }),
      )
      .output(z.object({ rules: z.array(MemoryRuleSchema), total: z.number() }))
      .route({
        method: "GET",
        path: "/harness/rules",
        summary: "List learned memory rules",
        description:
          "Pages through the assistant's learned rules (customer aliases, ship-to aliases, carrier terms and accounts, own-site addresses), filterable by kind, customer, status or text.",
      }),
    get: oc
      .input(z.object({ ruleId: z.uuid() }))
      .output(MemoryRuleSchema)
      .route({
        method: "GET",
        path: "/harness/rules/{ruleId}",
        summary: "Get a learned memory rule",
        description:
          "Returns one learned rule with its kind, customer scope, match key, payload, status, version history links and who activated or retired it.",
      }),
    update: oc
      .input(z.object({ ruleId: z.uuid(), payload: MemoryRulePayloadSchema }))
      .output(MemoryRuleSchema)
      .route({
        method: "POST",
        path: "/harness/rules/{ruleId}/update",
        summary: "Edit a learned memory rule",
        description:
          "Changes a learned rule's payload by activating a new version that supersedes the current one; returns the new rule.",
      }),
    retire: oc
      .input(
        z.object({ ruleId: z.uuid(), reason: z.string().min(1).max(2000) }),
      )
      .output(z.object({ success: z.boolean() }))
      .route({
        method: "POST",
        path: "/harness/rules/{ruleId}/retire",
        summary: "Retire a learned memory rule",
        description:
          "Deactivates a learned rule with a reason so order processing stops applying it; the rule's history is kept.",
      }),
    getUsages: oc
      .input(z.object({ ruleId: z.uuid(), ...Pagination }))
      .output(
        z.object({
          usages: z.array(MemoryRuleUsageWithRecordSchema),
          total: z.number(),
        }),
      )
      .route({
        method: "GET",
        path: "/harness/rules/{ruleId}/usages",
        summary: "List where a memory rule was applied",
        description:
          "Pages through the records, such as sales order drafts, that a learned rule was applied to, with links and labels.",
      }),
    routeTargets: oc
      .output(
        z.object({
          organizations: z.array(z.object({ id: z.uuid(), name: z.string() })),
        }),
      )
      .route({
        method: "GET",
        path: "/harness/route-targets",
        summary: "List organizations a sender route can target",
        description:
          "Returns the other organizations in the caller's tenant that a sender-route memory may route documents to. Readable by anyone who can propose memories.",
      }),
  },
  proposals: {
    list: oc
      .input(
        z.object({
          status: MemoryProposalStatusSchema.optional(),
          kind: MemoryProposalKindSchema.optional(),
          customerId: z.uuid().optional(),
          draftSalesOrderId: z.uuid().optional(),
          ...Pagination,
        }),
      )
      .output(
        z.object({
          proposals: z.array(MemoryProposalSchema),
          total: z.number(),
        }),
      )
      .route({
        method: "GET",
        path: "/harness/proposals",
        summary: "List memory rule proposals",
        description:
          "Pages through rules the assistant proposes learning from user corrections, filterable by status, kind, customer or sales order draft, with evidence and support counts.",
      }),
    get: oc
      .input(z.object({ proposalId: z.uuid() }))
      .output(MemoryProposalSchema)
      .route({
        method: "GET",
        path: "/harness/proposals/{proposalId}",
        summary: "Get a memory rule proposal",
        description:
          "Returns one learning proposal with its rationale, payload, supporting and contradicting evidence, and the active rule it would replace.",
      }),
    getSummary: oc
      .output(
        z.object({
          proposed: z.number(),
          accepted: z.number(),
          rejected: z.number(),
          superseded: z.number(),
          stale: z.number(),
        }),
      )
      .route({
        method: "GET",
        path: "/harness/proposals/summary",
        summary: "Count memory proposals by status",
        description:
          "Returns how many learning proposals are proposed, accepted, rejected, superseded or stale for the org.",
      }),
    create: oc
      .input(
        z.object({
          payload: MemoryProposalPayloadSchema,
          /**
           * The area an accepted rule applies in (sales orders when omitted).
           * Must be one the kind can be filed under (memoryModulesForProposal).
           */
          module: MemoryModuleSchema.optional(),
          title: z.string().min(3).max(200),
          rationale: z.string().min(1).max(4000),
          evidence: z
            .object({
              correctionEventIds: z.array(z.uuid()).max(200).optional(),
              draftSalesOrderIds: z.array(z.uuid()).max(50).optional(),
              threadIds: z.array(z.uuid()).max(50).optional(),
            })
            .optional(),
          /** Requires MemoryProposal.Approve; activates immediately. */
          acceptImmediately: z.boolean().optional(),
        }),
      )
      .output(MemoryProposalSchema)
      .route({
        method: "POST",
        path: "/harness/proposals",
        summary: "Propose a memory rule",
        description:
          "Creates a learning proposal (rule, note or skill draft) with title, rationale and evidence; with approve permission it can be activated immediately.",
      }),
    accept: oc
      .input(
        z.object({
          proposalId: z.uuid(),
          expectedRevision: z.number().int(),
          /** Reviewer edits applied before activation. */
          payload: MemoryProposalPayloadSchema.optional(),
        }),
      )
      .output(MemoryProposalSchema)
      .route({
        method: "POST",
        path: "/harness/proposals/{proposalId}/accept",
        summary: "Accept a memory rule proposal",
        description:
          "Approves a learning proposal, optionally with reviewer edits, and activates it as a rule or memory note; notes the decision on cited sales order drafts.",
      }),
    reject: oc
      .input(
        z.object({
          proposalId: z.uuid(),
          expectedRevision: z.number().int(),
          reason: z.string().min(1).max(4000),
        }),
      )
      .output(MemoryProposalSchema)
      .route({
        method: "POST",
        path: "/harness/proposals/{proposalId}/reject",
        summary: "Reject a memory rule proposal",
        description:
          "Rejects a learning proposal with a reason so it is not activated; notes the decision on cited sales order drafts.",
      }),
  },
  corrections: {
    list: oc
      .input(
        z.object({
          field: CorrectionFieldSchema.optional(),
          draftSalesOrderId: z.uuid().optional(),
          actorKind: CorrectionActorKindSchema.optional(),
          from: z.iso.datetime().optional(),
          to: z.iso.datetime().optional(),
          ...Pagination,
        }),
      )
      .output(
        z.object({ events: z.array(CorrectionEventSchema), total: z.number() }),
      )
      .route({
        method: "GET",
        path: "/harness/corrections",
        summary: "List user corrections",
        description:
          "Pages through human and agent corrections captured on records (customer, ship-to, product, shipping and freight fields), filterable by field, draft, actor and date range.",
      }),
    /**
     * The caller's own latest customer correction on a record, marked a
     * one-off (never learned from) and/or given a reason. Both are optional
     * answers to the prompt that follows a customer change.
     */
    annotate: oc
      .input(AnnotateCorrectionInputSchema)
      .output(z.object({ annotated: z.boolean() }))
      .route({
        method: "POST",
        path: "/harness/corrections/annotate",
        summary: "Annotate my latest correction",
        description:
          "Marks the current user's latest customer change on a cash receipt or remittance as a one-off (never learned from) and/or records the reason.",
      }),
  },
  brain: {
    get: oc.output(BrainSnapshotSchema).route({
      method: "GET",
      path: "/harness/brain",
      summary: "Get the assistant's learning overview",
      description:
        "Returns what the assistant has learned: counts of rules, notes, pending proposals and corrections, a growth timeline, a knowledge graph and recent activity.",
    }),
    /** Seed the loop from order history: derive, persist and cluster corrections now. */
    bootstrap: oc
      .input(
        z.object({
          days: z.number().int().min(1).max(3650).default(365),
          minSupport: z.number().int().min(1).max(10).default(2),
        }),
      )
      .output(BrainBootstrapResultSchema)
      .route({
        method: "POST",
        path: "/harness/brain/bootstrap",
        summary: "Learn from past order corrections",
        description:
          "Scans sales order drafts from the last N days, records the corrections people made, and creates or updates learning proposals with enough support.",
      }),
  },
  actions: {
    get: oc
      .input(z.object({ pendingActionId: z.uuid() }))
      .output(HarnessPendingActionSchema)
      .route({
        method: "GET",
        path: "/harness/actions/{pendingActionId}",
        summary: "Get a pending assistant action",
        description:
          "Returns one assistant action preview awaiting the user's Apply: tool, preview, target record, status and expiry.",
      }),
    apply: oc
      .input(z.object({ pendingActionId: z.uuid() }))
      .output(HarnessActionApplyResultSchema)
      .route({
        method: "POST",
        path: "/harness/actions/{pendingActionId}/apply",
        summary: "Apply a previewed assistant action",
        description:
          "Runs an assistant action the user approved after re-checking permissions and policy and refreshing the preview against the current record; returns applied or failed.",
      }),
    discard: oc
      .input(
        z.object({
          pendingActionId: z.uuid(),
          reason: z.string().max(500).optional(),
        }),
      )
      .output(z.object({ status: z.literal("discarded") }))
      .route({
        method: "POST",
        path: "/harness/actions/{pendingActionId}/discard",
        summary: "Discard a previewed assistant action",
        description:
          "Discards an assistant action preview without running it, with an optional reason; on a sales order it is recorded as a correction to learn from.",
      }),
    previewViewCommand: oc
      .input(
        z.object({
          /** The conversation whose view the button is in. */
          threadId: z.uuid(),
          /** The action tool's chat name, e.g. `approve_order`. */
          tool: z.string().min(1).max(200),
          /** The command's input; `{ "$param": name }` reads `params`. */
          input: z.record(z.string(), z.unknown()),
          params: z.record(z.string(), z.unknown()).default({}),
        }),
      )
      .output(HarnessActionToolResultSchema)
      .route({
        method: "POST",
        path: "/harness/actions/view-command",
        summary: "Preview an action from a chat view",
        description:
          "Runs a button in a view the assistant showed: plans the action as the current user and returns its preview to Apply, never applying it directly; returns failed when the user can't use the action.",
      }),
    undo: oc
      .input(z.object({ toolCallId: z.string().min(1).max(200) }))
      .output(HarnessActionApplyResultSchema)
      .route({
        method: "POST",
        path: "/harness/actions/undo",
        summary: "Undo an assistant action",
        description:
          "Reverses an action the assistant applied, within its undo window, as the current user; refuses if someone has since changed the same field.",
      }),
  },
  toolCalls: {
    summary: oc
      .input(HarnessToolCallFiltersSchema)
      .output(HarnessToolCallSummarySchema)
      .route({
        method: "GET",
        path: "/harness/tool-calls/summary",
        summary: "Summarize assistant tool usage",
        description:
          "Returns assistant tool-call totals and per-tool counts of writes, failures, discards and undos, filterable by module, page, tool, user, server or browser execution, effect, status, record and date range.",
      }),
    list: oc
      .input(HarnessToolCallFiltersSchema.extend(Pagination))
      .output(
        z.object({
          calls: z.array(HarnessToolCallSchema),
          total: z.number().int(),
        }),
      )
      .route({
        method: "GET",
        path: "/harness/tool-calls",
        summary: "List assistant tool calls",
        description:
          "Pages through assistant tool calls with module, page, tool, user, execution side, effect and outcome; non-admins see only their own.",
      }),
    get: oc
      .input(z.object({ toolCallId: z.string().min(1) }))
      .output(HarnessToolCallDetailSchema)
      .route({
        method: "GET",
        path: "/harness/tool-calls/{toolCallId}",
        summary: "Get an assistant tool call",
        description:
          "Returns one assistant tool call with its redacted input, result and outcome.",
      }),
  },
  policies: {
    list: oc.output(HarnessToolPoliciesSchema).route({
      method: "GET",
      path: "/harness/policies",
      summary: "List assistant action tool policies",
      description:
        "Lists every assistant action tool with its module, pages, default direct or preview behavior, required permissions, the org's override, and the per-turn write cap.",
    }),
    update: oc
      .input(
        z.object({
          toolType: z.string().min(1),
          mode: HarnessToolPolicyModeSchema,
        }),
      )
      .output(HarnessToolPoliciesSchema)
      .route({
        method: "POST",
        path: "/harness/policies/update",
        summary: "Set an assistant tool's policy",
        description:
          "Sets the org's override for one assistant action tool: inherit the default, disable it, or require a preview before it runs.",
      }),
    updateWriteCap: oc
      .input(
        z.object({
          maxWritesPerTurn: z.number().int().positive().max(100).nullable(),
        }),
      )
      .output(HarnessToolPoliciesSchema)
      .route({
        method: "POST",
        path: "/harness/policies/write-cap",
        summary: "Set the assistant's write limit per turn",
        description:
          "Sets or clears the maximum number of write actions the assistant may perform in one reply for the org.",
      }),
  },
  recommendations: {
    list: oc
      .input(
        z.object({
          pageKey: z.string().min(1),
          entityId: z.string().optional(),
        }),
      )
      .output(
        z.object({ recommendations: z.array(HarnessRecommendationSchema) }),
      )
      .route({
        method: "GET",
        path: "/harness/recommendations",
        summary: "List assistant recommendations for a page",
        description:
          "Returns suggested next steps for the current page and optional record, limited to what the user may do, each with severity and the prompt it sends to the assistant.",
      }),
  },
  drafts: {
    getInfluence: oc
      .input(z.object({ draftSalesOrderId: z.uuid() }))
      .output(DraftInfluenceSchema)
      .route({
        method: "GET",
        path: "/harness/drafts/{draftSalesOrderId}/influence",
        summary: "Explain AI influence on a sales order draft",
        description:
          "Returns how learning shaped one sales order draft: rules applied or excluded, AI reasoning records, proposals raised and human corrections captured.",
      }),
  },
};
